import { useState } from "react";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";

import { DeviceData } from "../../../../../../API";
import WarningBox from "../../../../../../common/components/box/WarningBox";
import CloseDialogIcon from "../../../../../../common/components/icons/CloseDialogIcon";
import {
  PAN_DEFAULT,
  TILT_DEFAULT,
  ZOOM_DEFAULT,
} from "../../../../../../common/components/inputs/ptz/constants";
import ControlTable from "../../../../../../common/components/stream/ControlTable";
import { useGetNonAdminAccess } from "../../../../../../common/hooks/useGetNonAdminAccess";
import { usePTController } from "../../../../../../common/hooks/usePTController";
import { useStream } from "../../../../../../common/hooks/useStream";
import { RouteEnum } from "../../../../../../common/models/enums";
import { extractDeviceNameFromId } from "../../../../../../common/utils/getEntityName";
import { selectedStreamObjectVariable } from "../../../../../../common/variables/selectedStreamObject";
import { usePublishNode } from "../../../../../devices/hooks/usePublishNode";
import HLSStreamPlayer from "../cameraView/HLSStreamPlayer";
import RouterLink from "./../../../../../../common/components/RouterLink";
import PTZoneListTableContainer from "./ptzonelist/PTZoneListTableContainer";
import {
  selectedZoneVariable,
  useFirstZoneVariable,
  useSelectedZoneVariable,
} from "./ptzonelist/zones.variable";

interface IDeviceImageListItemBarTitleProps {
  deviceName: string;
  deviceId: string;
  imageDate?: string;
  nodeId: string;
  locationName: string;
  deviceData: DeviceData;
  isReady: boolean;
  setIsReady: (value: boolean) => void;
  currentDevice: string;
  open: boolean;
  setOpen: (value: boolean) => void;
  onLiveViewClick: () => void;
}

const DeviceImageListItemBarTitle = ({
  deviceName,
  imageDate,
  deviceId,
  nodeId,
  locationName,
  deviceData,
  isReady,
  setIsReady,
  currentDevice,
  open,
  setOpen,
  onLiveViewClick: onLiveViewClick,
}: IDeviceImageListItemBarTitleProps): JSX.Element => {
  const { nonAdminView } = useGetNonAdminAccess();
  const selectedZone = useSelectedZoneVariable();
  const firstZoneVariable = useFirstZoneVariable();

  const { publishNode } = usePublishNode();
  const { getStreamName } = useStream();

  const [ptOpen, setPtOpen] = useState(false);
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState(false);

  const {
    pan,
    tilt,
    zoom,
    disabled,
    movePtUp,
    movePtDown,
    movePtLeft,
    movePtRight,
    handleGoToPt,
    handlePanInputChange,
    handleTiltInputChange,
    handleZoomInputChange,
    validationState,
  } = usePTController({
    deviceId,
    deviceName,
    nodeId,
    deviceData: deviceData,
    zoneName: selectedZone.zoneName,
  });

  const { extractedDeviceName } = extractDeviceNameFromId(deviceId);

  const handleOpenPtControlsOnClick = (): void => {
    publishNode({
      message: JSON.stringify({
        TARGET: "PANTILT",
        ACTION: "START_PT_CONTROL",
        device_name: extractedDeviceName,
      }),
      nodeId,
    }).then((): void => {
      setPtOpen(true);
    });
  };

  const handleClose = (): void => {
    const firstZonePan = firstZoneVariable?.pan ?? PAN_DEFAULT;
    const firstZoneTilt = firstZoneVariable?.tilt ?? TILT_DEFAULT;
    const firstZoneZoom = firstZoneVariable?.zoom ?? ZOOM_DEFAULT;
    const firstZoneName = firstZoneVariable?.zoneName ?? "";

    setOpen(false);

    setIsReady(false);

    setPtOpen(false);

    const streamName = getStreamName(nodeId, deviceId);

    publishNode({
      message: JSON.stringify({
        TARGET: "KINESIS_STREAM",
        ACTION: "KINESIS_STREAM_END",
        streamName,
        deviceName: extractedDeviceName,
      }),
      nodeId,
    }).then((): void => {
      setIsReady(true);

      selectedStreamObjectVariable(null);
    });

    if (ptOpen) {
      handleGoToPt(firstZonePan, firstZoneTilt, firstZoneZoom, firstZoneName);

      publishNode({
        message: JSON.stringify({
          TARGET: "PANTILT",
          ACTION: "STOP_PT_CONTROL",
          device_name: extractedDeviceName,
        }),
        nodeId,
      }).then((): void => {
        selectedZoneVariable({
          zoneName: firstZoneName,
          pan: firstZonePan,
          tilt: firstZoneTilt,
          zoom: firstZoneZoom,
        });
      });
    }
  };

  const handleSetVideoError = () => {
    setVideoError(true);
  };

  const { hasZoom, hasPanTilt } = deviceData;

  const videoPlayerWidth = videoLoading || videoError ? "100%" : "auto";

  return (
    <>
      <Dialog
        PaperProps={{
          sx: {
            borderRadius: "0px",
            maxWidth: "100%",
            maxHeight: "100%",
            width: "100%",
            height: "100%",
            margin: 0,
          },
        }}
        open={open}
        fullWidth
        onClose={handleClose}
      >
        <CloseDialogIcon onClose={handleClose} />
        <DialogContent
          sx={{
            display: "flex",
            width: "100%",
            height: "100%",
            overflowY: "hidden",
          }}
        >
          <Box
            sx={{
              minWidth: "50%",
              display: "flex",
              width: videoPlayerWidth,
            }}
          >
            <HLSStreamPlayer
              setDialogStyles
              setVideoLoading={setVideoLoading}
              setVideoError={handleSetVideoError}
            />
          </Box>
          <Box
            sx={{
              padding: "2em",
              paddingBottom: 0,
              minWidth: "412px",
            }}
          >
            {ptOpen ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                <WarningBox>
                  <Box
                    sx={{
                      display: "flex",
                      gap: "0.5em",
                      alignItems: "center",
                    }}
                  >
                    <ErrorOutlineIcon sx={{ color: "warning.main" }} />
                    <Typography
                      sx={{ color: "warning.main", "& p": { margin: 0 } }}
                      variant="body2Regular"
                      component="span"
                    >
                      <p>The model is stopped while you are</p>
                      <p>running PT controller</p>
                    </Typography>
                  </Box>
                </WarningBox>

                <Divider
                  sx={{
                    margin: ".7em 0",
                    visibility: "hidden",
                  }}
                />

                <Typography
                  sx={{
                    marginBottom: "1em",
                  }}
                  variant="subtitle1"
                >
                  Move the camera position
                </Typography>

                <ControlTable
                  pan={pan}
                  tilt={tilt}
                  zoom={zoom}
                  ptDisabled={disabled}
                  validation={validationState}
                  showValidationAlert
                  handlePanInputChange={handlePanInputChange}
                  handleGoToPt={handleGoToPt}
                  handleTiltInputChange={handleTiltInputChange}
                  handleZoomInputChange={handleZoomInputChange}
                  movePtLeft={movePtLeft}
                  movePtRight={movePtRight}
                  movePtUp={movePtUp}
                  movePtDown={movePtDown}
                  hasZoom={hasZoom}
                />

                <WarningBox>
                  <Box
                    sx={{
                      display: "flex",
                      gap: "0.5em",
                      alignItems: "center",
                    }}
                  >
                    <ErrorOutlineIcon
                      sx={{
                        color: "warning.main",
                      }}
                    />

                    <Typography
                      sx={{
                        color: "warning.main",
                        "& p": {
                          margin: 0,
                        },
                      }}
                      variant="body2Regular"
                      component="span"
                    >
                      <p>Please note that pan and tilt of device is</p>
                      <p>accurate to 0.1 degree</p>
                    </Typography>
                  </Box>
                </WarningBox>

                <Divider
                  sx={{
                    margin: ".7em 0",
                    visibility: "hidden",
                  }}
                />

                <Typography variant="body1Bold" component="span">
                  Observing zones
                </Typography>
                <PTZoneListTableContainer
                  currentDeviceId={currentDevice}
                  nodeId={nodeId}
                  hasZoom={hasZoom}
                  disableRowClick={disabled}
                  handleGoToPt={handleGoToPt}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell
                        variant="head"
                        sx={{ border: 0, width: "80px" }}
                      >
                        <Typography variant="inputLabel" component="span">
                          DEVICE
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ border: 0 }}>
                        <Typography variant="body2Bold" component="span">
                          {deviceName}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        variant="head"
                        sx={{ border: 0, width: "80px" }}
                      >
                        <Typography variant="inputLabel" component="span">
                          LOCATION
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ border: 0 }}>
                        <Typography variant="body2Regular" component="span">
                          {locationName}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                <Box
                  sx={{
                    marginTop: "auto",
                  }}
                >
                  {hasPanTilt && (
                    <>
                      <Button
                        sx={{
                          display: ptOpen ? "none" : "block",
                          color: "text.primary",
                          borderColor: "text.primary",
                          marginBottom: "1em",
                        }}
                        variant="outlined"
                        size="small"
                        onClick={handleOpenPtControlsOnClick}
                      >
                        <Typography variant="buttonMedium" component="span">
                          Open PT Controller
                        </Typography>
                      </Button>

                      <Box
                        sx={{
                          display: "flex",
                          gap: "0.5em",
                          paddingBottom: "2em",
                        }}
                      >
                        <ErrorOutlineIcon sx={{ color: "warning.main" }} />
                        <Typography
                          sx={{ color: "warning.main", "& p": { margin: 0 } }}
                          variant="body2Regular"
                          component="span"
                        >
                          <p>
                            All models are stopped while you are running pt
                            control
                          </p>
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          padding: "1em 0.5em 0 0.5em",
        }}
      >
        <Typography
          variant="body1Bold"
          sx={{
            "&:hover": {
              cursor: isReady ? "pointer" : "not-allowed",
              color: (theme): string => theme.palette.primary.main,
            },
          }}
          onClick={onLiveViewClick}
        >
          {deviceName}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: hasPanTilt ? "baseline" : "center",
          gap: "0.5em",
          margin: "1em 0.5em 0.25em",
        }}
      >
        <Typography variant="inputLabel" component="span">
          Location
        </Typography>
        <Typography
          variant="body2Regular"
          component="span"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {locationName}
        </Typography>
        {hasPanTilt && (
          <Button
            variant="text"
            sx={{
              flexShrink: 0,
              marginLeft: "auto",
              padding: "2px 4px",
              height: "26px",
            }}
          >
            <RouterLink
              title="Zones List"
              to={
                nonAdminView
                  ? `${
                      RouteEnum.SensoryLiveViewZoneListShort
                    }/${encodeURIComponent(deviceId)}`
                  : `${RouteEnum.ZoneListShort}/${encodeURIComponent(deviceId)}`
              }
              state={{
                deviceId,
              }}
            />
          </Button>
        )}
      </Box>
      {imageDate && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "0.5em",
            padding: "0 0.5em",
          }}
        >
          <Typography variant="inputLabel" component="span">
            Last keyframe
          </Typography>
          <Typography
            variant="body2Regular"
            component="span"
            sx={{ fontSize: "0.75em" }}
          >
            {imageDate}
          </Typography>
        </Box>
      )}
    </>
  );
};

export default DeviceImageListItemBarTitle;
